import { ApiError } from '../identity/types';
import { divRound, formatQuantity, formatUnits4Money, fromUnits4, toUnits4 } from './money';
import {
  HdcErrorCode,
  payBasisLabels,
  quantityUnits,
  rateLabels,
  type EarningDetail,
  type PayBasis,
  type RateSource,
  type ServiceLine,
  type ServiceRate,
} from './types';

/**
 * The mock server's rate resolution and pricing, a faithful stand-in for Module 6's pure resolver (contract sections 7 and 8). The real
 * resolver lives on the server; this exists so the mock gateways behave like the contract and the screens can be built and tested.
 *
 * Precedence at the line's service date:
 *   1 the employee's own rate for (discipline, pay basis): a service-type match beats "any", then a location match beats "any";
 *   2 a company rate for the line's location, then 3 a company default (location empty): a service-type match beats "any". In each, a row that names
 *     the line's worker category outranks a row for ANY worker category (discipline null), service type first inside each;
 *   4 the employee's base rate, only when its pay basis equals the line's.
 * Within the chosen tier and specificity there must be exactly ONE rate in effect: two equally specific rows are ambiguous and the line is
 * refused (RATE_AMBIGUOUS), never a silent pick. No match is RATE_NOT_FOUND, never a guess and never zero.
 *
 * All pricing is exact integer arithmetic (BigInt): the effective rate keeps up to 4 decimals and only the line AMOUNT is rounded, half away from zero, to whole cents.
 * `effectiveTo` is treated as the first day the row no longer applies, like the existing pay-rate assignments.
 */

export interface BaseRateInput {
  payBasis: PayBasis | null;
  /** Dollars, as Compensation stores it. */
  rate: number;
  label: string;
}

export interface ResolveInput {
  employeeId: string;
  line: ServiceLine;
  /** The service date the rate is resolved at (already defaulted and checked against the period). */
  serviceDate: string;
  employeeRates: readonly ServiceRate[];
  companyRates: readonly ServiceRate[];
  baseRate: BaseRateInput | null;
  disciplineName: string;
  disciplineCode: string;
  serviceTypeName: string | null;
  serviceTypeCode: string | null;
}

const problem = (status: number, code: string, detail: string, extra: Record<string, unknown> = {}) =>
  new ApiError(status, detail, null, true, code, { title: code, status, detail, code, ...extra });

export const inEffect = (rate: Pick<ServiceRate, 'effectiveFrom' | 'effectiveTo'>, date: string): boolean =>
  rate.effectiveFrom <= date && (rate.effectiveTo === null || rate.effectiveTo > date);

/** Whether two effective ranges overlap (half-open: a row that ends on the day another starts does not overlap it). */
export function rangesOverlap(
  a: Pick<ServiceRate, 'effectiveFrom' | 'effectiveTo'>,
  b: Pick<ServiceRate, 'effectiveFrom' | 'effectiveTo'>,
): boolean {
  return (b.effectiveTo === null || a.effectiveFrom < b.effectiveTo) && (a.effectiveTo === null || b.effectiveFrom < a.effectiveTo);
}

interface Pick1 {
  rate: ServiceRate;
  source: RateSource;
}

/** The best-specificity group of candidates (service type, then location). Several rows in that group is an ambiguity. */
function bestGroup(rows: ServiceRate[], line: ServiceLine, withLocation: boolean): ServiceRate[] {
  const score = (r: ServiceRate) => (r.serviceTypeId !== null ? 2 : 0) + (withLocation && r.workLocationId !== null ? 1 : 0);
  const compatible = rows.filter(
    (r) => (r.serviceTypeId === null || r.serviceTypeId === line.serviceTypeId)
      && (!withLocation || r.workLocationId === null || r.workLocationId === line.workLocationId),
  );
  if (compatible.length === 0) return [];
  const best = Math.max(...compatible.map(score));
  return compatible.filter((r) => score(r) === best);
}

function choose(group: ServiceRate[], source: RateSource): Pick1 | null {
  if (group.length === 0) return null;
  if (group.length > 1) {
    const ids = group.map((r) => r.id);
    throw problem(
      422, HdcErrorCode.RateAmbiguous,
      `More than one equally specific rate applies (rows ${ids.join(', ')}). The line was not priced. End or correct one of those rates so only one applies.`,
      { rateIds: ids },
    );
  }
  return { rate: group[0]!, source };
}

/** The rate plus its differential, in 1/10,000 dollars (never rounded to cents: only the line amount is). */
function effectiveUnits4(rate: ServiceRate): bigint {
  const base = toUnits4(rate.rate);
  if (rate.differentialKind === 'Amount' && rate.differentialValue !== null) return base + toUnits4(rate.differentialValue);
  if (rate.differentialKind === 'Percent' && rate.differentialValue !== null) {
    const percentHundredths = toUnits4(rate.differentialValue) / 100n; // 1/100 percent
    return divRound(base * (10000n + percentHundredths), 10000n);
  }
  return base;
}

const SOURCE_TEXT: Record<RateSource, string> = {
  EmployeeServiceRate: 'employee rate',
  CompanyLocationRate: 'company location rate',
  CompanyDefaultRate: 'company default rate',
  EmployeeBaseRate: 'employee base rate',
  AdHoc: 'rate entered on the line',
  BaseAssignment: 'base pay',
};

/** Picks the rate for one service line and prices it, or throws the contract's problem (RATE_NOT_FOUND / RATE_AMBIGUOUS). */
export function resolveServiceLine(input: ResolveInput): EarningDetail {
  const { line, serviceDate } = input;
  const matchesBasisAndDate = (r: ServiceRate) => r.payBasis === line.payBasis && inEffect(r, serviceDate);
  const matchesDiscipline = (r: ServiceRate) => r.disciplineId === line.disciplineId && matchesBasisAndDate(r);

  let picked = choose(bestGroup(input.employeeRates.filter(matchesDiscipline), line, true), 'EmployeeServiceRate');
  // Company tiers (universal compensation phase B): a row that names the line's worker category always outranks a row for ANY category.
  const company = input.companyRates.filter((r) => (r.disciplineId === line.disciplineId || r.disciplineId === null) && matchesBasisAndDate(r));
  const inTier = (rows: ServiceRate[], source: RateSource): Pick1 | null =>
    choose(bestGroup(rows.filter((r) => r.disciplineId !== null), line, false), source) ?? choose(bestGroup(rows.filter((r) => r.disciplineId === null), line, false), source);
  if (!picked && line.workLocationId) {
    picked = inTier(company.filter((r) => r.workLocationId === line.workLocationId), 'CompanyLocationRate');
  }
  if (!picked) picked = inTier(company.filter((r) => r.workLocationId === null), 'CompanyDefaultRate');

  const where = `${input.disciplineName}${input.serviceTypeName ? ` / ${input.serviceTypeName}` : ''}`;
  const unit = (line.quantity === 1 ? quantityUnits[line.payBasis].singular : quantityUnits[line.payBasis].plural);
  const quantityUnits4 = toUnits4(line.quantity);

  let rateDollars: number;
  let effective: bigint; // 1/10,000 dollars
  let source: RateSource;
  let ruleId: string | null;
  let differentialKind: ServiceRate['differentialKind'] = 'None';
  let differentialValue: number | null = null;
  let effectiveFrom: string | null = null;
  let anyCategory = false;
  if (picked) {
    rateDollars = picked.rate.rate;
    effective = effectiveUnits4(picked.rate);
    source = picked.source;
    ruleId = picked.rate.id;
    differentialKind = picked.rate.differentialKind;
    differentialValue = picked.rate.differentialValue;
    effectiveFrom = picked.rate.effectiveFrom;
    anyCategory = picked.rate.disciplineId === null;
  } else if (input.baseRate && input.baseRate.payBasis === line.payBasis) {
    rateDollars = input.baseRate.rate;
    effective = toUnits4(input.baseRate.rate);
    source = 'EmployeeBaseRate';
    ruleId = null;
  } else {
    throw problem(
      422, HdcErrorCode.RateNotFound,
      `Earning line: no ${rateLabels[line.payBasis]} is configured for ${where} on ${serviceDate}. Add an employee rate or a company rate for it (or a base rate with the same pay basis), then recalculate. No rate is guessed and none defaults to zero.`,
    );
  }

  // quantity (1/10,000) x effective rate (1/10,000 dollars) = 1e-8 dollars -> cents, rounded half away from zero. Only the amount is rounded.
  const amountCents = divRound(quantityUnits4 * effective, 1000000n);
  const effectiveDollars = fromUnits4(effective);
  const amount = fromUnits4(amountCents * 100n);

  const baseUnits4 = toUnits4(rateDollars);
  const differentialText = differentialKind === 'Percent' && differentialValue !== null
    ? ` ${formatUnits4Money(baseUnits4)} + ${formatQuantity(differentialValue)}% differential ${formatUnits4Money(effective - baseUnits4)}`
    : differentialKind === 'Amount' && differentialValue !== null
      ? ` ${formatUnits4Money(baseUnits4)} + ${formatUnits4Money(toUnits4(differentialValue))} differential`
      : '';
  const since = effectiveFrom ? `, effective ${effectiveFrom}` : '';
  const explanation =
    `${formatQuantity(line.quantity)} ${unit} x ${formatUnits4Money(effective)} (${rateLabels[line.payBasis]}${differentialText}, ${SOURCE_TEXT[source]}${anyCategory ? ' (any worker category)' : ''} for ${where}${since}) = ${formatUnits4Money(amountCents * 100n)}`;

  return {
    label: where,
    amount,
    payBasis: line.payBasis,
    quantity: line.quantity,
    rate: rateDollars,
    differentialKind: picked ? differentialKind : null,
    differentialValue,
    effectiveRate: effectiveDollars,
    rateSource: source,
    rateRuleId: ruleId,
    explanation,
    disciplineId: line.disciplineId,
    disciplineCode: input.disciplineCode,
    disciplineName: input.disciplineName,
    serviceTypeId: line.serviceTypeId,
    serviceTypeCode: input.serviceTypeCode,
    serviceTypeName: input.serviceTypeName,
    workedHours: line.workedHours,
    serviceDate,
    workLocationId: line.workLocationId,
    additionalEarningAuthorized: line.additionalEarningAuthorized,
    additionalEarningReason: line.additionalEarningReason,
  };
}

/**
 * The double-payment rule: two lines for the same (service date, discipline, service type) with DIFFERENT pay bases are refused unless
 * BOTH carry an explicit authorization with a reason. Line against line only: it does not compare with hours paid through a base assignment.
 */
export function assertNoDoublePayment(lines: ReadonlyArray<{ line: ServiceLine; serviceDate: string }>): void {
  for (let i = 0; i < lines.length; i += 1) {
    for (let j = i + 1; j < lines.length; j += 1) {
      const a = lines[i]!;
      const b = lines[j]!;
      const same = a.serviceDate === b.serviceDate && a.line.disciplineId === b.line.disciplineId && a.line.serviceTypeId === b.line.serviceTypeId;
      if (!same || a.line.payBasis === b.line.payBasis) continue;
      const authorized = (l: ServiceLine) => l.additionalEarningAuthorized && (l.additionalEarningReason ?? '').trim() !== '';
      if (authorized(a.line) && authorized(b.line)) continue;
      throw problem(
        422, HdcErrorCode.DoublePaymentBlocked,
        `Two lines pay the same work on ${a.serviceDate} on different bases (${payBasisLabels[a.line.payBasis]} and ${payBasisLabels[b.line.payBasis]}). ` +
          'Remove one of them, or mark both as an additional earning with a reason.',
      );
    }
  }
}
